'use client';

import './field.scss';

import { ReactNode, forwardRef, useEffect, useId } from 'react';
import {
  type FieldError,
  type RegisterOptions,
  type UseFormRegisterReturn,
  type UseFormReturn,
  useFormContext,
  useFormState,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useUnmount } from 'react-use';

import { ArkIcon, ArkIconProps } from '@sola/ark-icons/lib/ark-icon.js';
import { IconNames } from '@sola/schema/lib/icons/icon-names.js';
import { isDevelopmentEnvironment } from '@sola/shared-utils/misc.js';

import { cn } from '../../utils/helpers';
import {
  Tooltip,
  TooltipContent,
  TooltipContentProps,
  TooltipTrigger,
} from '../tooltip/tooltip';

/**
 * Returns field error element id based on field id.
 */
export const getErrorElId = (fieldId: string) => `err-${fieldId}`;

export interface FieldOutput {
  /**
   * Register options are only required within a form.
   */
  controlProps: Partial<UseFormRegisterReturn> & {
    id: string;
    /**
     * Only used when not in a form and checkbox needs to be controlled.
     */
    checked?: boolean;
    /**
     * Initial value.
     */
    defaultValue?: RegisterOptions['value'];
    /**
     * Only used when not in a form and field needs to be controlled.
     */
    value?: string | number | string[];
  };
  /**
   * Only available within a form.
   */
  error?: FieldError | undefined;
  /**
   * Only available within a form.
   */
  formMethods?: Omit<UseFormReturn, 'register' | 'setFocus'>;
}

export interface FieldProps extends Omit<RegisterOptions, 'label'> {
  children: (props: FieldOutput) => React.ReactNode;
  controlRef?: React.MutableRefObject<HTMLElement>;
  /**
   * This shouldn't be used for fields within a <Form/>, instead, you should be setting default values within the Form component's options prop.
   */
  defaultValue?: FieldOutput['controlProps']['defaultValue'];
  isFocused?: boolean;
  name: string;
  fieldClasses?: string;
  checked?: boolean;
}

export interface FieldWithFormProps extends FieldProps {
  id: string;
  context: UseFormReturn;
}
export const FieldWithForm: React.FC<FieldWithFormProps> = ({
  children,
  context,
  controlRef,
  defaultValue,
  id,
  isFocused,
  name,
  fieldClasses,
  shouldUnregister,
  ...fieldOptions
}) => {
  if (!name) {
    throw new Error('A field is missing a name');
  }
  if (defaultValue && isDevelopmentEnvironment) {
    console.error(
      `Anathema! Looks like you tried setting defaultValue directly on a field within a <Form/>. Instead you should be setting all default values within the Form options prop, which maps to args from RHF's useForm(). See https://react-hook-form.com/docs/useform#defaultValues`,
    );
  }
  const { register, setFocus, ...methods } = context;
  const { errors } = useFormState();
  const { t } = useTranslation();

  // Make the component controlled if value is passed as an option.
  // TODO: We should remove this, but for now leaving it until we have tests for backwards compatbility.
  useEffect(() => {
    if (!fieldOptions.value) {
      return;
    }

    methods.setValue(name, fieldOptions.value);
  }, [fieldOptions.value]);

  // Possibly unregister when unmounted.
  useUnmount(() => {
    if (shouldUnregister) {
      methods.unregister(name);
    }
  });

  // Programatically set focus.
  useEffect(() => {
    if (isFocused) {
      setFocus(name);
    }
  }, [isFocused]);

  // Set default required error message if necessary.
  const fieldError = errors[name];
  if (fieldError && !fieldError.message && fieldError.type === 'required') {
    fieldError.message = t('core.forms.errors.requiredField');
  }

  // Register field and pass necessary props to component.
  const { ref, ...registerProps } = register(
    name,
    fieldOptions as RegisterOptions,
  );
  const renderOutput = {
    controlProps: {
      id,
      required: !!fieldOptions.required,
      ...registerProps,
      ref: (e: HTMLElement) => {
        ref(e);
        if (!controlRef) {
          return;
        }
        // eslint-disable-next-line no-param-reassign
        controlRef.current = e;
      },
    },
    error: fieldError as FieldError | undefined,
    formMethods: methods,
  };

  return (
    <div
      className={cn('Field', {
        'is-required': fieldOptions.required,
        'has-error': fieldError,
        [`${fieldClasses}`]: fieldClasses,
      })}
    >
      {children(renderOutput)}
    </div>
  );
};

/**
 * @TODO this needs some docs and tidying up.
 */
export const Field: React.FC<FieldProps> = ({ ...props }) => {
  const id = useId();
  const formContext = useFormContext();

  if (!formContext) {
    return props.children({
      controlProps: {
        id,
        value: props.value,
        checked: props.checked,
        defaultValue: props.defaultValue,
      },
    });
  }

  return <FieldWithForm id={id} context={formContext} {...props} />;
};

export interface FieldWrapperProps extends React.PropsWithChildren {
  className?: string;
  isCentered?: boolean;
}

export const FieldWrapper: React.FC<FieldWrapperProps> = ({
  className,
  children,
  isCentered = true,
}) => {
  return (
    <div
      className={cn(
        'relative flex',
        isCentered ? 'items-center' : 'items-start',
        {
          [`${className}`]: !!className,
        },
      )}
    >
      {children}
    </div>
  );
};

export const FieldErrorIcon: React.FC<{ error?: FieldError }> = ({ error }) =>
  !error ? null : (
    <span className="Field__error-icon">
      <ArkIcon iconName="alert-circle" size="20" />
    </span>
  );

export const FieldErrorMessage: React.FC<{
  error?: FieldError;
  id: string;
}> = ({ error, id }) =>
  !error ? null : (
    <div id={id} className="Field__error mt-snug-lg" role="alert">
      {error.message as string}
    </div>
  );

export const FieldTooltipIcon: React.FC<{
  help?: string | ReactNode;
  tooltipFit?: TooltipContentProps['fitContent'];
  tooltipSize?: TooltipContentProps['size'];
}> = ({ help, tooltipFit, tooltipSize = 'sm' }) =>
  !help ? null : (
    <Tooltip>
      <TooltipTrigger>
        <button
          type="button"
          className="Field__tooltip-icon"
          aria-label="Additional information"
          onClick={(e) => {
            e.preventDefault();
          }}
        >
          <ArkIcon iconName="info-circle" size="20" />
        </button>
      </TooltipTrigger>
      <TooltipContent
        fitContent={tooltipFit}
        size={tooltipSize}
        className="Field__tooltip"
        alignOffset={-12}
        onPointerDownOutside={(e) => {
          e.preventDefault();
        }}
      >
        {help}
      </TooltipContent>
    </Tooltip>
  );

interface FieldIconProps {
  iconName?: IconNames;
  className?: string;
  label?: string;
  size?: ArkIconProps['size'];
  action?: () => void;
}

export const FieldIconButton = forwardRef<HTMLButtonElement, FieldIconProps>(
  ({ iconName, className, label, action, size = '20' }, ref) => {
    if (!iconName) {
      return null;
    }

    return (
      <button
        className="Field__icon-btn"
        title={label}
        aria-label={label}
        type="button"
        ref={ref}
        onClick={action}
      >
        <ArkIcon iconName={iconName} className={className} size={size} />
      </button>
    );
  },
);

export const FieldIcon: React.FC<FieldIconProps> = (props) => {
  const { iconName, className, label, action, size } = props;
  if (!iconName) {
    return null;
  }

  if (action) {
    return <FieldIconButton {...props} />;
  }

  return (
    <ArkIcon
      iconName={iconName}
      aria-label={label}
      size={size}
      className={cn('Field__icon', {
        [`${className}`]: !!className,
      })}
    />
  );
};
